import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import type { ProductGroup, CheckoutFormData } from "./types";
import {
  buildCartItemsWithDiscounts,
  calculateTotals,
  isVceSchoolLicence,
  VCE_SCHOOL_LICENCE_QUANTITY,
} from "./products";
import type { CartItem } from "./types";

const CART_STORAGE_KEY = "smartered-cart";

/**
 * Cart entry: a product group with a quantity (number of teachers) and optional variant
 */
export interface CartEntry {
  groupId: string;
  group: ProductGroup;
  quantity: number;
  variantId?: string; // e.g., "bronze" or "silver"
  isAddingToExisting?: boolean; // If true, skip initial tier (only add additional teachers)
}

/**
 * Reorder context: school info from validated HMAC link
 */
export interface ReorderContext {
  schoolId: string;
  schoolName: string;
}

/**
 * Quote context: pre-populated order info from validated quote link
 */
export interface QuoteContext {
  schoolId: string;
  schoolName: string;
  contact?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
  paymentMethod?: "card" | "invoice";
  manualDiscounts?: Array<{ description: string; amountCents: number }>;
  // H6 (docs/deep-analysis-2026-07-05.md): raw HMAC-signed quote link params
  // (`d`/`s` query strings). Carried through to checkout so the server can
  // re-verify discounts from the signed payload instead of trusting
  // client-supplied manualDiscounts.
  quoteData?: string;
  quoteSig?: string;
}

interface CartState {
  entries: Record<string, CartEntry>;
  checkoutData: CheckoutFormData | null;
  reorderContext: ReorderContext | null;
  quoteContext: QuoteContext | null;
}

interface CartContextValue {
  entries: CartEntry[];
  isEmpty: boolean;
  checkoutData: CheckoutFormData | null;
  reorderContext: ReorderContext | null;
  quoteContext: QuoteContext | null;

  // Actions
  addToCart: (group: ProductGroup, quantity: number, variantId?: string, isAddingToExisting?: boolean) => void;
  updateQuantity: (groupId: string, quantity: number) => void;
  updateVariant: (groupId: string, variantId: string) => void;
  removeFromCart: (groupId: string) => void;
  clearCart: () => void;
  setCheckoutData: (data: CheckoutFormData) => void;
  setReorderContext: (context: ReorderContext | null) => void;
  setQuoteContext: (context: QuoteContext | null) => void;

  // For submission (includes automatic discount line items)
  getCartItems: () => CartItem[];
  getTotals: () => ReturnType<typeof calculateTotals>;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * A VCE school licence is always one whole licence: 99 teacher seats with its
 * paid initial tier. Every write path (add, quantity update, restore from
 * sessionStorage) goes through this, so no quantity-1, zero-seat or
 * additional-only VCE purchase can be built. Other products pass through untouched.
 */
function pinSchoolLicence(entry: CartEntry): CartEntry {
  if (!isVceSchoolLicence(entry.group)) return entry;
  return { ...entry, quantity: VCE_SCHOOL_LICENCE_QUANTITY, isAddingToExisting: false };
}

function loadCartFromStorage(): CartState {
  try {
    const stored = sessionStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored) as CartState;
      const entries = Object.fromEntries(
        Object.entries(state.entries || {}).map(([groupId, entry]) => [groupId, pinSchoolLicence(entry)]),
      );
      return { ...state, entries };
    }
  } catch {
    // Ignore storage errors
  }
  return { entries: {}, checkoutData: null, reorderContext: null, quoteContext: null };
}

function saveCartToStorage(state: CartState) {
  try {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CartState>(loadCartFromStorage);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    saveCartToStorage(state);
  }, [state]);

  const addToCart = useCallback((group: ProductGroup, quantity: number, variantId?: string, isAddingToExisting?: boolean) => {
    // For existing subscriptions, quantity can be 0 (nothing to add)
    // For new subscriptions, minimum is 1
    if (quantity < 1 && !isAddingToExisting) return;
    if (quantity < 1 && isAddingToExisting) return; // Don't add 0 additional teachers

    setState((prev) => ({
      ...prev,
      entries: {
        ...prev.entries,
        [group.id]: pinSchoolLicence({ groupId: group.id, group, quantity, variantId, isAddingToExisting }),
      },
    }));
  }, []);

  const updateQuantity = useCallback((groupId: string, quantity: number) => {
    setState((prev) => {
      if (quantity < 1) {
        // Remove if quantity drops to 0
        const { [groupId]: _, ...rest } = prev.entries;
        return { ...prev, entries: rest };
      }

      const entry = prev.entries[groupId];
      if (!entry) return prev;

      return {
        ...prev,
        entries: {
          ...prev.entries,
          [groupId]: pinSchoolLicence({ ...entry, quantity }),
        },
      };
    });
  }, []);

  const updateVariant = useCallback((groupId: string, variantId: string) => {
    setState((prev) => {
      const entry = prev.entries[groupId];
      if (!entry) return prev;

      return {
        ...prev,
        entries: {
          ...prev.entries,
          [groupId]: { ...entry, variantId },
        },
      };
    });
  }, []);

  const removeFromCart = useCallback((groupId: string) => {
    setState((prev) => {
      const { [groupId]: _, ...rest } = prev.entries;
      return { ...prev, entries: rest };
    });
  }, []);

  const clearCart = useCallback(() => {
    setState({ entries: {}, checkoutData: null, reorderContext: null, quoteContext: null });
  }, []);

  const setCheckoutData = useCallback((data: CheckoutFormData) => {
    setState((prev) => ({ ...prev, checkoutData: data }));
  }, []);

  const setReorderContext = useCallback((context: ReorderContext | null) => {
    setState((prev) => ({ ...prev, reorderContext: context }));
  }, []);

  const setQuoteContext = useCallback((context: QuoteContext | null) => {
    setState((prev) => ({ ...prev, quoteContext: context }));
  }, []);

  const entries = Object.values(state.entries);
  const isEmpty = entries.length === 0;

  const getCartItems = useCallback(() => {
    const entriesArray = Object.values(state.entries).map((entry) => ({
      group: entry.group,
      quantity: entry.quantity,
      variantId: entry.variantId,
      isAddingToExisting: entry.isAddingToExisting,
    }));
    return buildCartItemsWithDiscounts(entriesArray);
  }, [state.entries]);

  const getTotals = useCallback(() => {
    const items = getCartItems();
    return calculateTotals(items);
  }, [getCartItems]);

  const value: CartContextValue = {
    entries,
    isEmpty,
    checkoutData: state.checkoutData,
    reorderContext: state.reorderContext,
    quoteContext: state.quoteContext,
    addToCart,
    updateQuantity,
    updateVariant,
    removeFromCart,
    clearCart,
    setCheckoutData,
    setReorderContext,
    setQuoteContext,
    getCartItems,
    getTotals,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
